/**
 * Main App Component
 * 
 * Root application component with routing, providers, and error boundary
 * Implements route-based code splitting for optimal performance
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './components/common/ToastContainer';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import { DataSourceProvider } from './contexts/DataSourceContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { VesselProvider } from './contexts/VesselContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AppShell from './components/layouts/AppShell';
import LoadingFallback from './components/common/LoadingFallback';

// Eagerly load critical pages for immediate access
import Login from './pages/Login';

// Lazy load Dashboard and all other pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));

// Development-only test pages (comment out in production)
// import ErrorBoundaryTest from './pages/ErrorBoundaryTest';

// Lazy load all other pages for code splitting
// Certificate pages
const CertificatesList = lazy(() => import('./features/certificates/pages/CertificatesList'));
const CertificatesExpiring = lazy(() => import('./features/certificates/pages/CertificatesExpiring'));

// Maintenance pages
const EquipmentList = lazy(() => import('./features/maintenance/pages/EquipmentList'));
const WorkOrderList = lazy(() => import('./features/maintenance/pages/WorkOrderList'));
const MaintenanceCalendar = lazy(() => import('./features/maintenance/pages/MaintenanceCalendar'));
const MaintenanceOverview = lazy(() => import('./features/maintenance/pages/MaintenanceOverview'));
const CriticalEquipmentDashboard = lazy(() => import('./features/maintenance/pages/CriticalEquipmentDashboard'));

// Crew pages
const PersonnelList = lazy(() => import('./features/crew/pages/PersonnelList'));
const CrewCertificates = lazy(() => import('./features/crew/pages/CrewCertificates'));
const WorkingHours = lazy(() => import('./features/crew/pages/WorkingHours'));
const Training = lazy(() => import('./features/crew/pages/Training'));

// Safety pages
const InspectionsList = lazy(() => import('./features/safety/pages/InspectionsList'));
const IncidentsList = lazy(() => import('./features/safety/pages/IncidentsList'));
const DrillsList = lazy(() => import('./features/safety/pages/DrillsList'));
const RiskAssessmentsList = lazy(() => import('./features/safety/pages/RiskAssessmentsList'));

// Inventory pages
const InventoryList = lazy(() => import('./features/inventory/pages/InventoryList'));
const InventoryTransactions = lazy(() => import('./features/inventory/pages/InventoryTransactions'));
const RequisitionsList = lazy(() => import('./features/inventory/pages/RequisitionsList'));

// Document pages
const DocumentsList = lazy(() => import('./features/documents/pages/DocumentsList'));
const FormsList = lazy(() => import('./features/documents/pages/FormsList'));

// Forms pages
const FormLibrary = lazy(() => import('./features/forms/pages/FormLibrary'));
const FormSubmissions = lazy(() => import('./features/forms/pages/FormSubmissions'));
const MissingFormsOverview = lazy(() => import('./features/forms/pages/MissingFormsOverview'));

// Corrective actions
const CorrectiveActionsList = lazy(() => import('./features/corrective-actions/pages/CorrectiveActionsList'));

// Tasks
const TasksDashboard = lazy(() => import('./features/tasks/pages/TasksDashboard'));

// Search
const GlobalSearch = lazy(() => import('./pages/GlobalSearch'));

// Logbook pages
const LogbookDashboard = lazy(() => import('./features/logbook/pages/LogbookDashboard'));
const LogbookList = lazy(() => import('./features/logbook/pages/LogbookList'));
const LogbookEntryCreate = lazy(() => import('./features/logbook/pages/LogbookEntryCreate'));
const LogbookEntryDetail = lazy(() => import('./features/logbook/pages/LogbookEntryDetail'));
const LogbookReports = lazy(() => import('./features/logbook/pages/LogbookReports'));
const WatchManagement = lazy(() => import('./features/logbook/pages/WatchManagement'));

// Environmental pages
const OilRecordBookPage = lazy(() => import('./features/environmental/pages/OilRecordBook'));
const GarbageRecordBookPage = lazy(() => import('./features/environmental/pages/GarbageRecordBook'));
const BallastWater = lazy(() => import('./features/environmental/pages/BallastWater'));
const Emissions = lazy(() => import('./features/environmental/pages/Emissions'));

// IHM pages
const IHMDashboard = lazy(() => import('./features/ihm/pages/IHMDashboard'));
const MaterialInventory = lazy(() => import('./features/ihm/pages/MaterialInventory'));
const SDSManagement = lazy(() => import('./features/ihm/pages/SDSManagement'));
const IHMInspections = lazy(() => import('./features/ihm/pages/IHMInspections'));
const RemovalPlanning = lazy(() => import('./features/ihm/pages/RemovalPlanning'));

// Navigation pages
const NavigationPositions = lazy(() => import('./pages/navigation/NavigationPositions'));
const Passages = lazy(() => import('./pages/navigation/Passages'));
const PortCalls = lazy(() => import('./pages/navigation/PortCalls'));

// Compliance pages
const AuditsList = lazy(() => import('./features/compliance/pages/AuditsList'));
const NonConformities = lazy(() => import('./features/compliance/pages/NonConformities'));

// System pages
const Reports = lazy(() => import('./features/reports/pages/Reports'));
const UsersList = lazy(() => import('./pages/users/UsersList'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Profile = lazy(() => import('./pages/Profile'));

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <DataSourceProvider>
            <VesselProvider>
              <ToastProvider position="top-right">
                <BrowserRouter>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/login" element={<Login />} />

                      {/* Protected Routes */}
                      <Route path="/" element={
                        <ProtectedRoute>
                          <AppShell />
                        </ProtectedRoute>
                      }>
                      {/* Dashboard */}
                      <Route index element={<Dashboard />} />

                      {/* Certificates */}
                      <Route path="certificates" element={<CertificatesList />} />
                      <Route path="certificates/expiring" element={<CertificatesExpiring />} />

                      {/* Maintenance */}
                      <Route path="maintenance" element={<MaintenanceOverview />} />
                      <Route path="maintenance/equipment" element={<EquipmentList />} />
                      <Route path="maintenance/work-orders" element={<WorkOrderList />} />
                      <Route path="maintenance/calendar" element={<MaintenanceCalendar />} />
                      <Route path="maintenance/critical-equipment" element={<CriticalEquipmentDashboard />} />

                      {/* Crew */}
                      <Route path="crew/personnel" element={<PersonnelList />} />
                      <Route path="crew/certificates" element={<CrewCertificates />} />
                      <Route path="crew/hours" element={<WorkingHours />} />
                      <Route path="crew/training" element={<Training />} />

                      {/* Safety */}
                      <Route path="safety/inspections" element={<InspectionsList />} />
                      <Route path="safety/incidents" element={<IncidentsList />} />
                      <Route path="safety/drills" element={<DrillsList />} />
                      <Route path="safety/risk-assessments" element={<RiskAssessmentsList />} />

                      {/* Inventory */}
                      <Route path="inventory" element={<InventoryList />} />
                      <Route path="inventory/transactions" element={<InventoryTransactions />} />
                      <Route path="inventory/requisitions" element={<RequisitionsList />} />

                      {/* Documents */}
                      <Route path="documents" element={<DocumentsList />} />
                      <Route path="documents/forms" element={<FormsList />} />

                      {/* Forms */}
                      <Route path="forms" element={<FormLibrary />} />
                      <Route path="forms/submissions" element={<FormSubmissions />} />
                      <Route path="forms/missing" element={<MissingFormsOverview />} />

                      {/* Logbook */}
                      <Route path="logbook" element={<LogbookDashboard />} />
                      <Route path="logbook/entries" element={<LogbookList />} />
                      <Route path="logbook/entries/new" element={<LogbookEntryCreate />} />
                      <Route path="logbook/entries/:id/edit" element={<LogbookEntryCreate />} />
                      <Route path="logbook/entries/:id" element={<LogbookEntryDetail />} />
                      <Route path="logbook/reports" element={<LogbookReports />} />
                      <Route path="logbook/watch-schedule" element={<WatchManagement />} />

                      {/* Environmental */}
                      <Route path="environmental/orb" element={<OilRecordBookPage />} />
                      <Route path="environmental/grb" element={<GarbageRecordBookPage />} />
                      <Route path="environmental/ballast" element={<BallastWater />} />
                      <Route path="environmental/emissions" element={<Emissions />} />

                      {/* IHM (Inventory of Hazardous Materials) */}
                      <Route path="ihm" element={<IHMDashboard />} />
                      <Route path="ihm/materials" element={<MaterialInventory />} />
                      <Route path="ihm/sds" element={<SDSManagement />} />
                      <Route path="ihm/inspections" element={<IHMInspections />} />
                      <Route path="ihm/removal-plan" element={<RemovalPlanning />} />

                      {/* Navigation */}
                      <Route path="navigation/positions" element={<NavigationPositions />} />
                      <Route path="navigation/passages" element={<Passages />} />
                      <Route path="navigation/port-calls" element={<PortCalls />} />

                      {/* Compliance */}
                      <Route path="compliance/audits" element={<AuditsList />} />
                      <Route path="compliance/non-conformities" element={<NonConformities />} />

                      {/* Corrective Actions */}
                      <Route path="corrective-actions" element={<CorrectiveActionsList />} />

                      {/* Tasks */}
                      <Route path="tasks" element={<TasksDashboard />} />

                      {/* Search */}
                      <Route path="search" element={<GlobalSearch />} />

                      {/* System */}
                      <Route path="reports" element={<Reports />} />
                      <Route path="users" element={<UsersList />} />
                      <Route path="settings" element={<Settings />} />
                      <Route path="profile" element={<Profile />} />

                      {/* Development-only test routes (comment out in production) */}
                      {/* <Route path="test/error-boundary" element={<ErrorBoundaryTest />} /> */}

                      {/* 404 - Catch-all redirect to dashboard */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </ToastProvider>
          </VesselProvider>
        </DataSourceProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
